// Enhanced smooth scrolling utility
export const smoothScrollTo = (target, duration = 800, offset = 80) => {
  const targetElement = typeof target === 'string' ? document.querySelector(target) : target;
  
  if (!targetElement) {
    console.warn('Smooth scroll target not found:', target);
    return;
  }

  const targetPosition = targetElement.offsetTop - offset;
  const startPosition = window.pageYOffset;
  const distance = targetPosition - startPosition;
  let startTime = null;

  const animation = currentTime => {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const run = easeInOutCubic(timeElapsed, startPosition, distance, duration);
    window.scrollTo(0, run);
    if (timeElapsed < duration) requestAnimationFrame(animation);
  };

  requestAnimationFrame(animation);
};

// Easing function for smooth acceleration/deceleration
const easeInOutCubic = (t, b, c, d) => {
  t /= d / 2;
  if (t < 1) return c / 2 * t * t * t + b;
  t -= 2;
  return c / 2 * (t * t * t + 2) + b;
};

// Smooth scroll to top
export const scrollToTop = (duration = 600) => {
  const startPosition = window.pageYOffset;
  const startTime = performance.now();

  const animation = currentTime => {
    const timeElapsed = currentTime - startTime;
    const run = easeInOutCubic(timeElapsed, startPosition, -startPosition, duration);
    window.scrollTo(0, run);
    if (timeElapsed < duration) requestAnimationFrame(animation);
  };

  requestAnimationFrame(animation);
};

// Initialize smooth scrolling for all anchor links
export const initSmoothScrolling = () => {
  // Force CSS smooth scrolling
  document.documentElement.style.scrollBehavior = 'smooth';
  document.body.style.scrollBehavior = 'smooth';
  
  // Handle all anchor links with hash
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (link) {
      const target = link.getAttribute('href');
      
      // Skip if it's just "#" (placeholder link)
      if (target === '#') {
        return;
      }
      
      // Skip if it's an external link or mailto/tel link
      if (link.getAttribute('target') === '_blank' || 
          target.startsWith('mailto:') || 
          target.startsWith('tel:')) {
        return;
      }
      
      e.preventDefault();
      smoothScrollTo(target);
    }
  });

  // Handle React Router navigation links
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="/"]');
    if (link && !link.getAttribute('href').includes('#')) {
      // Add smooth transition before navigation
      document.body.style.opacity = '0.8';
      document.body.style.transform = 'scale(0.98)';
      
      setTimeout(() => {
        document.body.style.opacity = '';
        document.body.style.transform = '';
      }, 150);
    }
  });

  // Override default scroll behavior
  const originalScrollTo = window.scrollTo;
  window.scrollTo = function(x, y) {
    // Handle both object and numeric parameters
    if (typeof x === 'object') {
      // ScrollToOptions object
      if (x.behavior !== 'instant') {
        x.behavior = 'smooth';
      }
      return originalScrollTo.call(this, x);
    } else {
      // Numeric parameters (x, y)
      return originalScrollTo.call(this, x, y);
    }
  };

  // Handle programmatic navigation (React Router)
  const handleProgrammaticScroll = (target) => {
    setTimeout(() => {
      if (target && target.startsWith('#')) {
        smoothScrollTo(target);
      }
    }, 100);
  };

  // Expose to window for React Router integration
  window.smoothScrollTo = smoothScrollTo;
  window.scrollToTop = scrollToTop;
  window.handleProgrammaticScroll = handleProgrammaticScroll;
  
  // Force smooth scrolling on all elements
  const style = document.createElement('style');
  style.textContent = `
    html, body, * {
      scroll-behavior: smooth !important;
    }
  `;
  document.head.appendChild(style);
};

// Smooth scroll for specific sections
export const scrollToSection = (sectionId, offset = 80) => {
  smoothScrollTo(`#${sectionId}`, 800, offset);
};

// Smooth scroll for elements with specific classes
export const scrollToElement = (selector, offset = 80) => {
  smoothScrollTo(selector, 800, offset);
}; 